import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { TrashCan } from "../../../components/Atomos/Icons/TrashCan";
import CloseModal from "../../../components/Atomos/Icons/EditMode/CloseModal";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import DropDownMultiple, {
  DropDownOptionBase,
} from "../../../components/staff/DropDownMultiple";
import Button from "../../../components/staff/Button";
import InputText from "../../../components/staff/InputText";
import NewTooltip from "../../../components/Atomos/Tooltip/NewTooltip";
import { Appstore } from "../../../redux";
import { fetchPersonByFirm } from "../../../Services/Person";
import { syncDataAdminsForFirm } from "../../../Services/DataAdmin";
import { toastError, toastPromise } from "../../../utils/toast";
import { formatDateLongString } from "../../../utils/dates";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import type { DataAdminView } from "../../../interfaces/models/IDataAdmin";
import type { FirmContactView } from "../../../interfaces/models/IFirmContact";
import {
  collapseOfficeRowsToAssignments,
  ensurePrimaryOfficeDataAdminRow,
  expandDataAdminsToOfficeRows,
  formatOfficeBlockHeading,
  getDataAdminPersonPickerOptions,
  mergeDataAdminBlocksByTier,
  normalizeDataAdminRowsForSave,
  resolveDataAdminDisplayTier,
  type DataAdminOfficeBlock,
  type DataAdminOfficeRow,
  type DataAdminTier,
  type PersonOfficeLike,
  type ProfessionalPickerOption,
} from "./manageDataAdminOfficeRules";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

type ManageDataAdminProps = {
  dataAdminData: DataAdminView[];
  offices: IOfficeNew[];
  membershipType?: string;
  primaryContact?: FirmContactView["primaryContact"];
  onClose?: () => void;
  onSaved?: (data: DataAdminView[]) => void;
};

function isPrimaryOffice(office: IOfficeNew): boolean {
  return String(office.membershipType ?? "").trim().toLowerCase() === "primary";
}

function serializeRows(rows: DataAdminOfficeRow[]): string {
  return JSON.stringify(
    rows.map((row) => ({
      officeId: row.officeId,
      personId: row.personId,
    })),
  );
}

function hasDuplicatePersonInBlock(rows: DataAdminOfficeRow[]): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    const personId = String(row.personId ?? "").trim();
    if (!personId) {
      continue;
    }
    if (seen.has(personId)) {
      return true;
    }
    seen.add(personId);
  }
  return false;
}

const ManageDataAdmin = ({
  dataAdminData,
  offices,
  membershipType,
  primaryContact,
  onClose,
  onSaved,
}: ManageDataAdminProps) => {
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);
  const firmId = manageFirm.selectedFirm?.id ?? dataAdminData[0]?.firmId ?? "";
  const { isStaffMode } = useStaffMode();

  const initialRows = useMemo(
    () => expandDataAdminsToOfficeRows(dataAdminData),
    [dataAdminData],
  );
  const rowsWithPrimaryFallback = useMemo(
    () =>
      ensurePrimaryOfficeDataAdminRow(initialRows, {
        offices,
        membershipType,
        primaryContact,
      }),
    [initialRows, offices, membershipType, primaryContact],
  );

  const [editableRows, setEditableRows] =
    useState<DataAdminOfficeRow[]>(rowsWithPrimaryFallback);
  const [originalSnapshot, setOriginalSnapshot] = useState(() =>
    serializeRows(initialRows),
  );
  const [persons, setPersons] = useState<PersonOfficeLike[]>([]);

  useEffect(() => {
    const rows = expandDataAdminsToOfficeRows(dataAdminData);
    setOriginalSnapshot(serializeRows(rows));
    setEditableRows(
      ensurePrimaryOfficeDataAdminRow(rows, {
        offices,
        membershipType,
        primaryContact,
      }),
    );
  }, [dataAdminData, offices, membershipType, primaryContact]);

  useEffect(() => {
    if (!firmId) {
      return;
    }
    let isMounted = true;

    fetchPersonByFirm(firmId)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setPersons(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isMounted = false;
    };
  }, [firmId]);

  const displayBlocks = useMemo(
    () => mergeDataAdminBlocksByTier(dataAdminData, offices),
    [dataAdminData, offices],
  );

  const peopleById = useMemo(
    () => new Map(persons.map((person) => [String(person.id ?? ""), person])),
    [persons],
  );

  const firmPersonOptions = useMemo(
    () => getDataAdminPersonPickerOptions(persons, offices),
    [offices, persons],
  );

  const getRowOptions = (
    tier: DataAdminTier,
    rowKey: string,
    currentPersonId: string,
  ): DropDownOptionBase[] => {
    const taken = new Set(
      editableRows
        .filter(
          (row) =>
            resolveDataAdminDisplayTier(row.officeId, offices) === tier &&
            row.rowKey !== rowKey &&
            String(row.personId ?? "").trim() !== "",
        )
        .map((row) => String(row.personId)),
    );
    const current = String(currentPersonId ?? "").trim();

    return firmPersonOptions.filter((option: ProfessionalPickerOption) => {
      const id = String(option.id);
      if (current !== "" && id === current) {
        return true;
      }
      return !taken.has(id);
    });
  };

  const rowsForTier = (tier: DataAdminTier) =>
    editableRows.filter(
      (row) => resolveDataAdminDisplayTier(row.officeId, offices) === tier,
    );

  const handleSelectPerson =
    (rowKey: string) => (value: DropDownOptionBase | null) => {
      const selected = value ? peopleById.get(String(value.id)) : undefined;

      setEditableRows((prev) =>
        prev.map((row) =>
          row.rowKey === rowKey
            ? {
                ...row,
                personId: selected ? String(selected.id ?? "") : "",
                name: selected
                  ? `${selected.name ?? ""} ${selected.lastName ?? ""}`.trim()
                  : "",
                firmEmail: String(selected?.firmEmail ?? ""),
                lastLogin: null,
                generatedId:
                  typeof selected?.generatedId === "number"
                    ? selected.generatedId
                    : selected?.generatedId != null
                      ? Number(selected.generatedId)
                      : undefined,
              }
            : row,
        ),
      );
    };

  const handleAddRow = (officeId: string) => {
    setEditableRows((prev) => [
      ...prev,
      {
        officeId,
        personId: "",
        rowKey: `new-${officeId}-${Date.now()}-${prev.length}`,
      },
    ]);
  };

  const handleDeleteRow = (rowKey: string) => {
    setEditableRows((prev) =>
      ensurePrimaryOfficeDataAdminRow(
        prev.filter((row) => row.rowKey !== rowKey),
        { offices, membershipType, primaryContact },
      ),
    );
  };

  const handleSave = async () => {
    if (!firmId) {
      return;
    }

    for (const block of displayBlocks) {
      const tier = resolveDataAdminDisplayTier(
        String(block.office.id ?? ""),
        offices,
      );
      const blockRows = rowsForTier(tier);

      if (blockRows.some((row) => !String(row.personId ?? "").trim())) {
        toastError(
          `Select a person for each Data Admin in ${formatOfficeBlockHeading(block.office)}.`,
        );
        return;
      }

      if (hasDuplicatePersonInBlock(blockRows)) {
        toastError(
          `Each person can only appear once in ${formatOfficeBlockHeading(block.office)}.`,
        );
        return;
      }
    }

    const nextSnapshot = serializeRows(editableRows);
    if (nextSnapshot === originalSnapshot) {
      onClose?.();
      return;
    }

    const assignments = collapseOfficeRowsToAssignments(
      normalizeDataAdminRowsForSave(editableRows, offices),
    );

    const updatedData = await toastPromise(
      syncDataAdminsForFirm(firmId, assignments),
      {
        pending: "Saving Data Admin changes...",
        success: "Data Admins updated successfully.",
      },
    );

    const syncedRows = expandDataAdminsToOfficeRows(updatedData);
    setEditableRows(syncedRows);
    setOriginalSnapshot(serializeRows(syncedRows));
    onSaved?.(updatedData);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="divide-y divide-gray-font">
        {displayBlocks.map(({ office }: DataAdminOfficeBlock) => {
          const officeId = String(office.id ?? "").trim();
          const tier = resolveDataAdminDisplayTier(officeId, offices);
          const heading = formatOfficeBlockHeading(office);
          const isPrimary = isPrimaryOffice(office);
          const blockRows = rowsForTier(tier);

          return (
            <div key={officeId} className="flex flex-col gap-2">
              <div className="flex mt-6 w-full gap-2 justify-between items-center ">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <label
                    className={`text-base font-semibold ${isStaffMode ? "text-secondary" : "text-primary"}`}
                  >
                    {heading}
                  </label>
                  {isPrimary ? (
                    <NewTooltip text="Data admins from main office can manage other offices" />
                  ) : null}
                </div>
                <Button
                  text="Add Data Admin"
                  color={isStaffMode ? "secondary" : "primary"}
                  variant="outlined"
                  size="fixed"
                  icon={<Plus />}
                  onClick={() => handleAddRow(officeId)}
                  labelNonVisible={["xxs", "xs"]}
                  className="!w-auto shrink-0"
                />
              </div>

              <div className="flex flex-col">
                {blockRows.length === 0 ? (
                  <div className="text-sm text-gray-300 italic py-2">
                    No data admins assigned
                  </div>
                ) : (
                  blockRows.map((row) => (
                    <div key={row.rowKey} className="flex">
                      <div className="grid grid-cols-12 gap-6 pb-2 pt-3 w-full">
                        <div className="col-span-12 md:col-span-6 lg:col-span-4">
                          <DropDownMultiple
                            title="Data Admin"
                            options={getRowOptions(
                              tier,
                              row.rowKey,
                              row.personId,
                            )}
                            value={row.personId || null}
                            renderInPortal
                            onChange={handleSelectPerson(row.rowKey)}
                          />
                        </div>
                        <div className="col-span-12 md:col-span-6 lg:col-span-4">
                          <InputText
                            title="Email"
                            readOnly
                            value={String(row.firmEmail || "")}
                          />
                        </div>
                        <div className="col-span-12 md:col-span-6 lg:col-span-4 flex">
                          <InputText
                            title="Last Login"
                            readOnly
                            value={
                              row.lastLogin
                                ? formatDateLongString(Number(row.lastLogin))
                                : "-"
                            }
                          />
                        </div>
                      </div>
                      <div className="flex items-start md:items-center">
                        <Button
                          color="primary"
                          variant="ghost"
                          size="fixed"
                          icon={<TrashCan />}
                          onClick={() => handleDeleteRow(row.rowKey)}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col md:flex-row w-full justify-end gap-4">
        <Button
          text="Cancel"
          color="neutral"
          variant="outlined"
          size="fixed"
          icon={<CloseModal />}
          onClick={() => onClose?.()}
        />
        <Button
          text="Update Data Admin(s)"
          color={isStaffMode ? "secondary" : "primary"}
          variant="filled"
          size="fixed"
          icon={<SaveChanges />}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default ManageDataAdmin;
